/**
 * SubscriptionDetailSheet
 *
 * A right-panel drawer that renders rich details for a single subscription.
 * Provides lifecycle action buttons (pause, resume, cancel, confirm payment)
 * and navigation to linked transactions.
 *
 * [T107] Subscription detail sheet with status-tinted hero section.
 */
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useCurrency } from '@/hooks/useCurrency';
import {
  DollarSign,
  RefreshCw,
  CalendarDays,
  CalendarRange,
  FileText,
  Clock,
  Pencil,
  Pause,
  Play,
  X,
  CreditCard,
  ExternalLink,
  Trash2,
  Layers,
} from 'lucide-react';
import type { Subscription } from '@/lib/types';
import { TemplateLinkedCard } from '@/components/templates/TemplateLinkedCard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SubscriptionDetailSheetProps {
  subscription: Subscription | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (sub: Subscription) => void;
  onDelete: (id: string) => void;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onCancel: (id: string) => void;
  onConfirmPayment: (id: string) => void;
  onViewTransactions: (sub: Subscription) => void;
  onUnlink?: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Visual config per status
// ---------------------------------------------------------------------------

const statusConfig: Record<
  string,
  { heroBg: string; badgeClass: string }
> = {
  active: {
    heroBg: 'bg-green-50 dark:bg-green-950/30',
    badgeClass:
      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  paused: {
    heroBg: 'bg-yellow-50 dark:bg-yellow-950/30',
    badgeClass:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  },
  cancelled: {
    heroBg: 'bg-red-50 dark:bg-red-950/30',
    badgeClass:
      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  expired: {
    heroBg: 'bg-gray-50 dark:bg-gray-900/40',
    badgeClass:
      'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function cycleLabel(sub: Subscription): string {
  const map: Record<string, string> = {
    weekly: 'week',
    biweekly: '2 weeks',
    monthly: 'month',
    quarterly: 'quarter',
    yearly: 'year',
  };
  if (sub.frequency === 'custom' && sub.customIntervalDays)
    return `${sub.customIntervalDays} days`;
  return map[sub.frequency] ?? sub.frequency;
}

function formatDateRange(startDate?: string, endDate?: string): string {
  if (!startDate) return '';
  const start = new Date(startDate).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  if (endDate) {
    const end = new Date(endDate).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${start} — ${end}`;
  }
  return `Since ${start} (indefinite)`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ---------------------------------------------------------------------------
// DetailRow helper
// ---------------------------------------------------------------------------

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/60">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
          {label}
        </p>
        <div className="text-sm text-foreground">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SubscriptionDetailSheet({
  subscription,
  open,
  onOpenChange,
  onEdit,
  onDelete,
  onPause,
  onResume,
  onCancel,
  onConfirmPayment,
  onViewTransactions,
  onUnlink,
}: SubscriptionDetailSheetProps) {
  const { fmt, currency: userCurrency } = useCurrency();

  if (!subscription) return null;

  const sub = subscription;
  const config = statusConfig[sub.status] ?? statusConfig.expired;
  const isActive = sub.status === 'active';
  const isPaused = sub.status === 'paused';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[calc(100vw-3rem)] sm:max-w-[480px] h-[100dvh] flex flex-col p-0 gap-0 overflow-hidden pb-[env(safe-area-inset-bottom)]"
      >
        {/* ---------------------------------------------------------------- */}
        {/* Hero area                                                         */}
        {/* ---------------------------------------------------------------- */}
        <div className={`px-6 pt-8 pb-6 ${config.heroBg}`}>
          <SheetHeader className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/80 dark:bg-black/20 shadow-sm shrink-0 text-2xl leading-none">
                {sub.emoji || sub.merchantName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <SheetTitle className="text-left leading-tight break-words">
                  {sub.merchantName}
                </SheetTitle>
                <span
                  className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${config.badgeClass}`}
                >
                  {sub.status}
                </span>
              </div>
            </div>

            {/* Amount */}
            <div>
              <span className="text-3xl font-bold tabular-nums">
                {fmt(sub.amount)}
              </span>
              <span className="ml-1.5 text-sm text-muted-foreground">
                / {cycleLabel(sub)}
              </span>
            </div>
          </SheetHeader>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Scrollable detail rows                                            */}
        {/* ---------------------------------------------------------------- */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Billing Amount */}
          <DetailRow icon={DollarSign} label="Billing Amount">
            <span>{fmt(sub.amount)}</span>
            {sub.currency !== userCurrency && (
              <Badge variant="outline" className="ml-1.5 text-xs">
                {sub.currency}
              </Badge>
            )}
          </DetailRow>

          {/* Billing Cycle */}
          <DetailRow icon={RefreshCw} label="Billing Cycle">
            {capitalize(cycleLabel(sub))}
          </DetailRow>

          {/* Next Billing Date */}
          {sub.nextBillingDate && (
            <DetailRow icon={CalendarDays} label="Next Billing Date">
              {new Date(sub.nextBillingDate).toLocaleDateString(undefined, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </DetailRow>
          )}

          {/* Date Range */}
          {sub.startDate && (
            <DetailRow icon={CalendarRange} label="Date Range">
              {formatDateRange(sub.startDate, sub.endDate)}
            </DetailRow>
          )}

          {/* Transaction Template */}
          {sub.templateId && (
            <DetailRow icon={Layers} label="Transaction Template">
              <TemplateLinkedCard
                templateId={sub.templateId}
                templateName={sub.templateId}
                onUnlink={() => onUnlink?.(sub._id)}
              />
            </DetailRow>
          )}

          {/* Notes */}
          {sub.notes && (
            <DetailRow icon={FileText} label="Notes">
              <span className="whitespace-pre-wrap break-words">{sub.notes}</span>
            </DetailRow>
          )}

          <Separator />

          {/* Created */}
          <DetailRow icon={Clock} label="Created">
            {new Date(sub.createdAt).toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </DetailRow>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Footer actions                                                    */}
        {/* ---------------------------------------------------------------- */}
        <SheetFooter className="sticky bottom-0 border-t bg-background/80 backdrop-blur-md px-4 sm:px-6 py-3 gap-2 flex-row flex-wrap">
          {/* Delete — far left */}
          <Button
            variant="outline"
            size="sm"
            className="min-h-[44px] mr-auto text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
            onClick={() => {
              onOpenChange(false);
              onDelete(sub._id);
            }}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete
          </Button>

          {/* Lifecycle buttons */}
          {isActive && (
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px]"
              onClick={() => {
                onOpenChange(false);
                onConfirmPayment(sub._id);
              }}
            >
              <CreditCard className="h-4 w-4 mr-1.5" />
              Confirm
            </Button>
          )}
          {isActive && (
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px]"
              onClick={() => {
                onOpenChange(false);
                onPause(sub._id);
              }}
            >
              <Pause className="h-4 w-4 mr-1.5" />
              Pause
            </Button>
          )}
          {isPaused && (
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px]"
              onClick={() => {
                onOpenChange(false);
                onResume(sub._id);
              }}
            >
              <Play className="h-4 w-4 mr-1.5" />
              Resume
            </Button>
          )}
          {(isActive || isPaused) && (
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px]"
              onClick={() => {
                onOpenChange(false);
                onCancel(sub._id);
              }}
            >
              <X className="h-4 w-4 mr-1.5" />
              Cancel
            </Button>
          )}

          {/* Transactions + Edit — far right */}
          <Button
            variant="ghost"
            size="sm"
            className="min-h-[44px]"
            onClick={() => {
              onOpenChange(false);
              onViewTransactions(sub);
            }}
          >
            <ExternalLink className="h-4 w-4 mr-1.5" />
            Transactions
          </Button>
          <Button
            size="sm"
            className="min-h-[44px]"
            onClick={() => {
              onOpenChange(false);
              onEdit(sub);
            }}
          >
            <Pencil className="h-4 w-4 mr-1.5" />
            Edit
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
